import React, { useState, useEffect } from 'react';
import { topicAPI, exerciseAPI, adminAPI } from '../api/client';
import { Edit, Trash2, Plus, ChevronDown, ChevronUp, Users, BookOpen, Lock, Unlock, UserX, ArrowUp, ArrowDown, Shield } from 'lucide-react';
import { ExerciseEditor } from './ExerciseEditor';
import { TwoFactorSettings } from './TwoFactorSettings';
import { ColorPalette } from './ColorPalette';
import { FONT_SIZE_OPTIONS } from '../config/fontSizes';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useLevel } from '../context/LevelContext';
import { useAuth } from '../context/AuthContext';

// Bot usage is a unified weekly cost budget (Claude + TTS + STT), not a token count.
// Costs arrive from the bot in micros: 1 micro = $0.000001.
const MICROS_PER_USD = 1000000;

const formatUsd = (micros) => `$${(Math.max(0, Number(micros) || 0) / MICROS_PER_USD).toFixed(2)}`;

const getBotBudget = (student) => {
  const limitMicros = Math.max(0, Number(student.botWeeklyLimitMicros) || 0);
  const usedMicros = Math.max(0, Number(student.botWeeklyCostMicros) || 0);

  if (!limitMicros) {
    // No limit synced yet (student never used the bot, or the bot has the weekly cap off)
    return { synced: !!student.botUsageSyncedAt, unlimited: true, usedMicros };
  }

  const percentLeft = Math.max(0, Math.min(100, Math.round(((limitMicros - usedMicros) / limitMicros) * 100)));

  return {
    synced: true,
    unlimited: false,
    usedMicros,
    limitMicros,
    percentLeft,
    exhausted: usedMicros >= limitMicros,
    // Design-system CSS vars, not Tailwind classes: this project overrides the
    // Tailwind palette, so text-gold/text-mint don't resolve to any color.
    color: percentLeft <= 15 ? 'var(--coral)' : (percentLeft <= 40 ? 'var(--gold)' : 'var(--mint)')
  };
};

const BotUsageSummary = ({ student }) => {
  const budget = getBotBudget(student);
  const messages = Number(student.botMessageCount || 0).toLocaleString('ru-RU');
  const resetAt = student.botWeekResetAt ? new Date(student.botWeekResetAt) : null;

  if (!budget.synced) {
    return (
      <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
        🤖 Расход: нет данных от бота
        {' · '}
        сообщений: <span className="font-bold" style={{ color: 'var(--mint)' }}>{messages}</span>
      </p>
    );
  }

  if (budget.unlimited) {
    return (
      <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
        🤖 Недельный лимит выключен · потрачено{' '}
        <span className="font-bold" style={{ color: 'var(--gold)' }}>{formatUsd(budget.usedMicros)}</span>
        {' · '}
        сообщений: <span className="font-bold" style={{ color: 'var(--mint)' }}>{messages}</span>
      </p>
    );
  }

  return (
    <div className="mt-2" style={{ maxWidth: 320 }}>
      <div className="flex items-center justify-between text-xs mb-1" style={{ color: 'var(--muted)' }}>
        <span>🤖 Бюджет недели</span>
        <span className="font-bold" style={{ color: budget.color }}>
          {budget.exhausted ? 'исчерпан' : `осталось ${budget.percentLeft}%`}
        </span>
      </div>

      <div
        className="rounded-full overflow-hidden"
        style={{ height: 6, background: 'rgba(255, 255, 255, 0.1)' }}
      >
        <div
          className="rounded-full"
          style={{
            height: '100%',
            width: `${100 - budget.percentLeft}%`,
            background: budget.color,
            transition: 'width 0.3s ease'
          }}
        />
      </div>

      <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
        <span className="font-bold text-white">{formatUsd(budget.usedMicros)}</span> из {formatUsd(budget.limitMicros)}
        {resetAt && ` · сброс ${resetAt.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}`}
        {' · '}
        сообщений: <span className="font-bold" style={{ color: 'var(--mint)' }}>{messages}</span>
      </p>

      <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
        Claude {formatUsd(student.botCostClaudeMicros)}
        {' · '}
        Озвучка {formatUsd(student.botCostTtsMicros)}
        {' · '}
        Распознавание {formatUsd(student.botCostSttMicros)}
      </p>
    </div>
  );
};

export const AdminPanel = () => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const { selectedLevel, changeLevel, levels } = useLevel();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('topics'); // 'topics', 'students', 'teachers', or 'security'
  const [topics, setTopics] = useState([]);
  const [students, setStudents] = useState([]);
  const [studentsPage, setStudentsPage] = useState(1);
  const [studentsLimit, setStudentsLimit] = useState(50);
  const [studentsTotal, setStudentsTotal] = useState(null);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentSearchInput, setStudentSearchInput] = useState('');
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [showTopicForm, setShowTopicForm] = useState(false);
  const [showExerciseEditor, setShowExerciseEditor] = useState(null);
  const [showStudentForm, setShowStudentForm] = useState(false);
  const [showTeacherForm, setShowTeacherForm] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [editingTopic, setEditingTopic] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const [topicFormData, setTopicFormData] = useState({
    title: '',
    germanTitle: '',
    description: '',
    order: 1,
    titleColor: '#7c3aed',
    titleFontSize: 'text-lg',
  });

  const [teacherFormData, setTeacherFormData] = useState({
    name: '',
    email: '',
    password: '',
  });

  const [studentFormData, setStudentFormData] = useState({
    name: '',
    email: '',
    password: '',
  });

  useEffect(() => {
    fetchTopics();
    fetchStats();
    // load first page of students
    fetchStudents(true);
    if (user?.role === 'admin') {
      fetchTeachers();
    }
  }, [user, language]);

  const fetchTopics = async () => {
    try {
      // Fast initial load: fetch topics without exercises to render UI quickly
      const response = await topicAPI.getAllTopics({ language, includeExercises: false });
      console.log('Fetched topics (light):', response.data);
      setTopics(response.data);
      // Notify other parts of the app that topics changed
      try { window.dispatchEvent(new CustomEvent('topicsUpdated')); } catch (e) { /* ignore in non-browser env */ }

      // Background prefetch: restore exercises for topics without blocking UI
      const allTopicIds = response.data.map(t => t.id);
      // Limit to first 100 topics to avoid too many concurrent requests
      const topicIdsToPrefetch = allTopicIds.slice(0, 100);
      prefetchExercisesForTopics(topicIdsToPrefetch, 5).catch(err => console.error('Prefetch error', err));
    } catch (error) {
      console.error('Error fetching topics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchExercisesForTopic = async (topicId) => {
    try {
      const response = await exerciseAPI.getExercises(topicId);
      const exercises = response.data;
      setTopics(prev => prev.map(t => t.id === topicId ? { ...t, exercises } : t));
    } catch (error) {
      console.error('Error fetching exercises for topic', topicId, error);
    }
  };

  const prefetchExercisesForTopics = async (topicIds = [], concurrency = 5) => {
    let i = 0;
    while (i < topicIds.length) {
      const batch = topicIds.slice(i, i + concurrency);
      await Promise.all(batch.map(id => fetchExercisesForTopic(id)));
      i += concurrency;
      // short pause between batches to avoid spikes
      await new Promise(r => setTimeout(r, 150));
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchStudents = async (reset = false, queryOverride = null) => {
    try {
      setLoadingStudents(true);
      const pageToLoad = reset ? 1 : studentsPage;
      const query = queryOverride !== null ? queryOverride : studentSearchQuery;
      const response = await adminAPI.getUsers({
        page: pageToLoad,
        limit: studentsLimit,
        q: query.trim() || undefined
      });
      const { users, total, page, limit } = response.data;
      if (reset) {
        setStudents(users);
        setStudentsPage(2);
      } else {
        setStudents(prev => [...prev, ...users]);
        setStudentsPage(page + 1);
      }
      setStudentsTotal(total);
      setStudentsLimit(limit);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleStudentSearch = () => {
    const normalized = studentSearchInput.trim();
    setStudentSearchQuery(normalized);
    fetchStudents(true, normalized);
  };

  const fetchTeachers = async () => {
    try {
      const response = await adminAPI.getTeachers();
      setTeachers(response.data);
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  const handleCreateTeacher = async () => {
    try {
      await adminAPI.createTeacher(teacherFormData);
      setTeacherFormData({
        name: '',
        email: '',
        password: '',
      });
      setShowTeacherForm(false);
      fetchTeachers();
      alert(t('teacherCreatedSuccess'));
    } catch (error) {
      console.error('Error creating teacher:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Неизвестная ошибка';
      alert(t('errorCreatingTeacher') + ': ' + errorMessage);
    }
  };

  const handleBlockTeacher = async (teacherId) => {
    try {
      await adminAPI.blockTeacher(teacherId);
      fetchTeachers();
      alert(t('teacherBlockedSuccess'));
    } catch (error) {
      console.error('Error blocking teacher:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Неизвестная ошибка';
      alert('Ошибка блокировки: ' + errorMessage);
    }
  };

  const handleUnblockTeacher = async (teacherId) => {
    try {
      await adminAPI.unblockTeacher(teacherId);
      fetchTeachers();
      alert(t('teacherUnblockedSuccess'));
    } catch (error) {
      console.error('Error unblocking teacher:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Неизвестная ошибка';
      alert('Ошибка разблокировки: ' + errorMessage);
    }
  };

  const handleDeleteTeacher = async (teacherId, teacherName) => {
    if (window.confirm(`${t('deleteTeacherConfirm')} ${teacherName}? ${t('cannotUndoAction')}`)) {
      try {
        await adminAPI.deleteTeacher(teacherId);
        fetchTeachers();
        alert(t('teacherDeletedSuccess'));
      } catch (error) {
        console.error('Error deleting teacher:', error);
        const errorMessage = error.response?.data?.error || error.message || 'Неизвестная ошибка';
        alert('Ошибка удаления: ' + errorMessage);
      }
    }
  };

  const handleEditTopic = (topic) => {
    setEditingTopic(topic);
    setTopicFormData({
      title: topic.title,
      germanTitle: topic.germanTitle,
      description: topic.description,
      order: topic.order,
      titleColor: topic.titleColor || '#7c3aed',
      titleFontSize: topic.titleFontSize || 'text-lg',
    });
    setShowTopicForm(true);
  };

  const handleCreateTopic = async () => {
    try {
      // Копируем germanTitle в title автоматически и используем текущий уровень
      const topicData = {
        ...topicFormData,
        title: topicFormData.germanTitle,
        level: selectedLevel,
        language: language
      };
      
      if (editingTopic) {
        // Update existing topic
        await topicAPI.updateTopic(editingTopic.id, topicData);
      } else {
        // Create new topic
        await topicAPI.createTopic(topicData);
      }
      
      setTopicFormData({
        title: '',
        germanTitle: '',
        description: '',
        order: 1,
        titleColor: '#7c3aed',
        titleFontSize: 'text-lg',
      });
      setShowTopicForm(false);
      setEditingTopic(null);
      await fetchTopics();
    } catch (error) {
      alert(t('errorCreatingTopic') + ' ' + error.response?.data?.error);
    }
  };

  const handleDeleteTopic = async (id) => {
    if (window.confirm(t('deleteTopicConfirm'))) {
      try {
        await topicAPI.deleteTopic(id);
        await fetchTopics();
      } catch (error) {
        const errorMessage = error.response?.data?.error || error.message || '';
        alert(`${t('errorDeletingTopic')} ${errorMessage}`.trim());
      }
    }
  };

  const handleMoveTopic = async (topicId, direction) => {
    try {
      console.log(`🔄 Moving topic ${topicId} ${direction}`);
      const response = await topicAPI.reorderTopic(topicId, direction);
      console.log('✓ Reorder response:', response);
      
      try {
        console.log('📚 Fetching updated topics...');
        await fetchTopics();
        console.log('✓ Topics fetched successfully');
      } catch (fetchError) {
        console.error('✗ Error fetching topics after reorder:', fetchError);
        throw fetchError;
      }
    } catch (error) {
      console.error('✗ Ошибка перемещения темы:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Something went wrong!';
      console.error('Error details:', {
        message: errorMessage,
        status: error.response?.status,
        data: error.response?.data,
        axiosError: error.isAxiosError
      });
      alert(`Ошибка перемещения уроков: ${errorMessage}`);
    }
  };

  const handleNormalizeOrders = async () => {
    if (window.confirm('Нормализовать порядок всех тем? Это переставит их в порядке создания.')) {
      try {
        await topicAPI.normalizeOrders();
        await fetchTopics();
        alert('Порядок тем успешно нормализован');
      } catch (error) {
        alert('Error normalizing orders: ' + error.response?.data?.error);
      }
    }
  };

  const handleDeleteExercise = async (exerciseId, topicId) => {
    if (window.confirm(t('deleteExerciseConfirm'))) {
      try {
        await exerciseAPI.deleteExercise(exerciseId);
        // Refresh exercises for this topic only
        await fetchExercisesForTopic(topicId);
      } catch (error) {
        alert(t('errorDeletingExercise') + ': ' + error.response?.data?.error);
      }
    }
  };

  const handleExerciseSaved = async () => {
    if (showExerciseEditor) {
      await fetchExercisesForTopic(showExerciseEditor);
    } else {
      await fetchTopics();
    }
    setShowExerciseEditor(null);
    setEditingExercise(null);
  };

  const handleBlockStudent = async (userId) => {
    try {
      await adminAPI.blockUser(userId);
      fetchStudents();
      alert('Студент заблокирован');
    } catch (error) {
      alert('Ошибка блокировки: ' + error.response?.data?.error);
    }
  };

  const handleCreateStudent = async () => {
    try {
      await adminAPI.createStudent(studentFormData);
      setStudentFormData({
        name: '',
        email: '',
        password: '',
      });
      setShowStudentForm(false);
      fetchStudents(true);
      fetchStats();
      alert(t('studentCreatedSuccess'));
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Неизвестная ошибка';
      alert(t('errorCreatingStudent') + ': ' + errorMessage);
    }
  };

  const handleUnblockStudent = async (userId) => {
    try {
      await adminAPI.unblockUser(userId);
      fetchStudents();
      alert('Студент разблокирован');
    } catch (error) {
      alert('Ошибка разблокировки: ' + error.response?.data?.error);
    }
  };

  const handleDeleteStudent = async (userId, userName) => {
    if (window.confirm(`Вы уверены, что хотите удалить студента ${userName}? Это действие нельзя отменить.`)) {
      try {
        await adminAPI.deleteUser(userId);
        fetchStudents();
        fetchStats();
        alert('Студент удален');
      } catch (error) {
        alert('Ошибка удаления: ' + error.response?.data?.error);
      }
    }
  };

  const handleToggleTelegramAccess = async (student) => {
    try {
      const nextEnabled = !student.telegramBotEnabled;
      await adminAPI.setTelegramAccess(student.id, nextEnabled);
      fetchStudents(true);
      alert(nextEnabled ? 'Доступ к Telegram-боту включен' : 'Доступ к Telegram-боту отключен');
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Ошибка изменения доступа';
      alert(errorMessage);
    }
  };

  if (loading) {
    return <div className="text-center p-8">{t('loading')}</div>;
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1 className="admin-title">{t('adminPanelTitle')}</h1>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          onClick={() => setActiveTab('topics')}
          className={`admin-tab-button topics ${activeTab === 'topics' ? 'active' : ''}`}
        >
          <BookOpen size={20} />
          {t('tabTopics')}
        </button>
        <button
          onClick={() => setActiveTab('students')}
          className={`admin-tab-button students ${activeTab === 'students' ? 'active' : ''}`}
        >
          <Users size={20} />
          {t('tabStudents')}
        </button>
        {user?.role === 'admin' && (
          <button
            onClick={() => setActiveTab('teachers')}
            className={`admin-tab-button teachers ${activeTab === 'teachers' ? 'active' : ''}`}
          >
            <Users size={20} />
            {t('tabTeachers')}
          </button>
        )}
        {user?.role === 'admin' && (
          <button
            onClick={() => setActiveTab('security')}
            className={`admin-tab-button security ${activeTab === 'security' ? 'active' : ''}`}
          >
            <Shield size={20} />
            {t('tabSecurity')}
          </button>
        )}
      </div>

      {/* Level Tabs (только для вкладки Темы) */}
      {activeTab === 'topics' && (
        <div className="admin-tabs">
          {levels.map((level) => (
            <button
              key={level}
              onClick={() => changeLevel(level)}
              className={`admin-tab-button ${selectedLevel === level ? 'active' : ''}`}
              style={selectedLevel === level ? {
                background: 'linear-gradient(135deg, rgba(232,184,75,0.2) 0%, rgba(78,205,196,0.2) 100%)',
              } : {}}
            >
              📚 {level}
            </button>
          ))}
        </div>
      )}

      {/* Topics Tab */}
      {activeTab === 'topics' && (
        <>
          {/* Topic Count Widget */}
          <div className="admin-widget mb-6">
            <span className="admin-widget-label">{t('totalTopicFolders')}</span>
            <div className="admin-widget-value">
              {topics.filter(topic => topic.level === selectedLevel && (topic.germanTitle.toLowerCase().includes(searchQuery.toLowerCase()) || topic.title.toLowerCase().includes(searchQuery.toLowerCase()))).length}
            </div>
          </div>

          <div className="admin-section-title">
            <h2 className="admin-section-heading">{t('topicsSection')}</h2>
            <button
              onClick={() => {
                setEditingTopic(null);
                setTopicFormData({
                  title: '',
                  germanTitle: '',
                  description: '',
                  order: 1,
                  titleColor: '#7c3aed',
                  titleFontSize: 'text-lg',
                });
                setShowTopicForm(!showTopicForm);
              }}
              className="admin-add-button"
            >
              <Plus size={20} />
              <span>{t('addTopic')}</span>
            </button>
            <button
              onClick={handleNormalizeOrders}
              className="ml-2 px-2 py-1 text-xs border border-gold rounded text-gold hover:text-mint transition"
              title="Нормализовать порядок тем"
            >
              Нормализовать
            </button>
          </div>

          {showTopicForm && (
          <div className="concept-card mb-6">
            <h3 className="text-lg font-semibold text-gold mb-4">
              {editingTopic ? t('editTopic') : t('createNewTopic')}
            </h3>



            {/* Preview - Topic Card appearance */}
            <div
              className="concept-card border-t-4 border-gold rounded-xl p-4 mb-4"
            >
              <p className="text-muted text-xs font-bold mb-1">
                {t('preview')}
              </p>
              <p
                className={`font-bold ${topicFormData.titleFontSize}`}
                style={{ color: topicFormData.titleColor }}
              >
                {topicFormData.germanTitle || t('germanTitle')}
              </p>
            </div>
            
            {/* Color and Font Size for Topic Title */}
            <div className="mb-4">
              <label className="text-gold text-sm font-bold mb-2 block uppercase tracking-wide">
                {t('topicTitleColor')}
              </label>
              <ColorPalette
                selectedColor={topicFormData.titleColor}
                onSelectColor={(color) => setTopicFormData({ ...topicFormData, titleColor: color })}
                size="w-9 h-9"
              />
            </div>
            
            <div className="mb-4">
              <label className="text-gold text-sm font-bold mb-2 block uppercase tracking-wide">
                {t('titleFontSizeLabel')}
              </label>
              <select
                value={topicFormData.titleFontSize}
                onChange={(e) => setTopicFormData({ ...topicFormData, titleFontSize: e.target.value })}
                className="w-full p-2 border-2 bg-white/5 border-border text-white rounded font-bold"
              >
                {FONT_SIZE_OPTIONS.map(opt => (
                  <option key={opt.token} value={opt.token}>{t(opt.labelKey)}</option>
                ))}
              </select>
            </div>

            
            <input
              type="text"
              placeholder={t('germanTitle')}
              value={topicFormData.germanTitle}
              onChange={(e) => setTopicFormData({ ...topicFormData, germanTitle: e.target.value })}
              style={{ color: topicFormData.titleColor }}
              className={`w-full p-2 border-2 bg-white/5 border-border rounded mb-4 font-bold ${topicFormData.titleFontSize}`}
            />
            
            <textarea
              placeholder={t('description')}
              value={topicFormData.description}
              onChange={(e) => setTopicFormData({ ...topicFormData, description: e.target.value })}
              className="w-full p-2 border-2 bg-white/5 border-border text-white rounded mb-4 font-bold"
            />
            <div className="mb-4 p-3 rounded concept-card">
              <p className="text-sm font-bold text-muted">
                {t('levelLabel')} <span className="text-gold">{editingTopic ? editingTopic.level : selectedLevel}</span>
              </p>
              <p className="text-xs text-muted mt-1">
                {editingTopic 
                  ? t('editingTopicLevel')
                  : t('topicWillBeCreated')
                }
              </p>
            </div>
            
            <div className="flex space-x-4">
              <button
                onClick={handleCreateTopic}
                className="bg-mint/15 border border-mint/30 text-mint px-4 py-2 rounded hover:bg-mint/25 transition font-bold"
              >
                {editingTopic ? t('save') : t('create')}
              </button>
              <button
                onClick={() => {
                  setShowTopicForm(false);
                  setEditingTopic(null);
                  setTopicFormData({
                    title: '',
                    germanTitle: '',
                    description: '',
                    order: 1,
                    titleColor: '#7c3aed',
                    titleFontSize: 'text-lg',
                  });
                }}
                className="bg-card border border-border text-muted px-4 py-2 rounded hover:border-coral transition font-bold"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-6">
          <input
            type="text"
            placeholder={t('searchTopics')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border-2 font-bold bg-white/5 border-border text-white placeholder-muted focus:outline-none focus:border-gold transition"
          />
        </div>

        <div className="space-y-4">
          {topics.filter(topic => 
            topic.level === selectedLevel &&
            (topic.germanTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
             topic.title.toLowerCase().includes(searchQuery.toLowerCase()))
          ).length === 0 ? (
            <div className="text-center py-12 concept-card rounded-lg">
              <div className="text-6xl mb-4">📚</div>
              <p className="font-bold text-lg mb-2 text-white">
                {searchQuery ? t('topicsNotFound') : `${t('noTopicsForLevel')} ${selectedLevel}`}
              </p>
              <p className="text-sm text-muted">
                {searchQuery ? t('tryChangeQuery') : t('createFirstTopic')}
              </p>
            </div>
          ) : (
            topics.filter(topic => 
              topic.level === selectedLevel &&
              (topic.germanTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
               topic.title.toLowerCase().includes(searchQuery.toLowerCase()))
            ).map((topic, index) => {
            const sameLevelTopics = topics
              .filter(t => t.level === topic.level)
              .sort((a, b) => a.order - b.order);
            const isFirstInLevel = sameLevelTopics.length > 0 && sameLevelTopics[0].id === topic.id;
            const isLastInLevel = sameLevelTopics.length > 0 && sameLevelTopics[sameLevelTopics.length - 1].id === topic.id;
            
            return (
            <div
              key={topic.id}
              className="concept-card border-t-4 border-gold rounded-xl p-4"
            >
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  {(user?.role === 'admin' || user?.role === 'teacher') && (
                  <div className="flex flex-col space-y-1">
                    <button
                      onClick={() => handleMoveTopic(topic.id, 'up')}
                      className="text-gold hover:text-mint disabled:opacity-30 disabled:cursor-not-allowed transition"
                      disabled={isFirstInLevel}
                      title="Переместить вверх"
                    >
                      <ArrowUp size={18} />
                    </button>
                    <button
                      onClick={() => handleMoveTopic(topic.id, 'down')}
                      className="text-gold hover:text-mint disabled:opacity-30 disabled:cursor-not-allowed transition"
                      disabled={isLastInLevel}
                      title="Переместить вниз"
                    >
                      <ArrowDown size={18} />
                    </button>
                  </div>
                  )}
                  <div>
                    <h3 
                      className={`font-bold ${topic.titleFontSize || 'text-lg'}`}
                      style={{ color: topic.titleColor || '#7c3aed' }}
                    >
                      {topic.germanTitle}
                    </h3>
                    <p className="text-muted text-sm">{topic.description} • {t('level')} {topic.level}</p>
                    <p className="text-muted text-sm">{t('exercisesSection')}: {topic.exercises?.length || 0}</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditTopic(topic)}
                    className="text-mint hover:text-gold transition"
                    title="Редактировать тему"
                  >
                    <Edit size={20} />
                  </button>
                  <button
                    onClick={async () => {
                      const opening = selectedTopic?.id !== topic.id;
                      if (opening && (!topic.exercises || topic.exercises.length === 0)) {
                        await fetchExercisesForTopic(topic.id);
                      }
                      setSelectedTopic(opening ? topic : null);
                    }}
                    className="text-gold hover:text-mint flex items-center space-x-1 transition"
                  >
                    {selectedTopic?.id === topic.id ? (
                      <ChevronUp size={20} />
                    ) : (
                      <ChevronDown size={20} />
                    )}
                  </button>
                  <button
                    onClick={() => handleDeleteTopic(topic.id)}
                    className="text-coral hover:text-red-400 transition"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>

              {selectedTopic?.id === topic.id && (
                <div className="mt-4 pt-4 border-t-2 border-border">
                  <h4 className="font-semibold text-gold mb-4">{t('exercisesSection')}</h4>
                  <div className="space-y-2 mb-4">
                    {topic.exercises?.map((exercise) => (
                      <div key={exercise.id} className="concept-card p-3 rounded flex justify-between items-center hover:border-gold/50 transition">
                        <div className="flex-1">
                          <p className="text-white font-bold">{exercise.title}</p>
                          <p className="text-xs text-muted">{exercise.description}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs bg-mint/20 text-mint px-2 py-1 rounded font-bold">
                            {exercise.type}
                          </span>
                          <button
                            onClick={() => {
                              console.log('Editing exercise:', exercise);
                              setEditingExercise(exercise);
                              setShowExerciseEditor(topic.id);
                            }}
                            className="text-gold hover:text-mint font-bold p-1 transition"
                            title={t('editExercise')}
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleDeleteExercise(exercise.id, topic.id)}
                            className="text-coral hover:text-red-400 font-bold p-1 transition"
                            title={t('deleteExercise')}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      setEditingExercise(null);
                      setShowExerciseEditor(topic.id);
                    }}
                    className="bg-gold/15 border border-gold/30 text-gold px-4 py-2 rounded flex items-center space-x-2 hover:bg-gold/25 transition font-bold w-full justify-center"
                  >
                    <Plus size={20} />
                    <span>{t('addExercise')}</span>
                  </button>
                </div>
              )}
            </div>
          );
          })
          )}
        </div>
        </>
      )}

      {/* Students Tab */}
      {activeTab === 'students' && (
        <div className="concept-card rounded-lg p-8 mb-8">
          {/* Student Count Widget */}
          <div className="concept-card p-6 rounded-lg mb-6">
            <p className="text-muted text-sm font-bold uppercase tracking-wide">{t('totalStudents')}</p>
            <p className="text-3xl font-bold text-mint">{students.length}</p>
          </div>

          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gold">
              {t('manageStudents')}
            </h2>
            {user?.role === 'admin' && (
              <button
                onClick={() => setShowStudentForm(!showStudentForm)}
                className="flex items-center gap-2 px-4 py-2 bg-mint/15 border border-mint/30 hover:bg-mint/25 text-mint rounded-lg font-bold transition"
              >
                <Plus size={20} />
                {t('createStudent')}
              </button>
            )}
          </div>

          <div className="flex gap-2 mb-6">
            <input
              type="text"
              value={studentSearchInput}
              onChange={(e) => setStudentSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleStudentSearch();
                }
              }}
              className="flex-1 p-3 border-2 rounded-lg bg-white/5 border-border text-white"
              placeholder="Поиск по имени или email ученика"
            />
            <button
              onClick={handleStudentSearch}
              className="px-4 py-2 bg-gold/15 border border-gold/30 text-gold rounded-lg font-bold hover:bg-gold/25 transition"
            >
              Найти
            </button>
            {studentSearchQuery && (
              <button
                onClick={() => {
                  setStudentSearchInput('');
                  setStudentSearchQuery('');
                  fetchStudents(true, '');
                }}
                className="px-4 py-2 bg-card border border-border hover:border-coral text-muted rounded-lg font-bold transition"
              >
                Сброс
              </button>
            )}
          </div>

          {showStudentForm && user?.role === 'admin' && (
            <div className="concept-card rounded-lg p-6 mb-6">
              <h3 className="text-xl font-bold mb-4 text-gold">
                {t('createNewStudent')}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block mb-2 font-bold text-gold uppercase tracking-wide text-sm">
                    {t('studentName')}:
                  </label>
                  <input
                    type="text"
                    value={studentFormData.name}
                    onChange={(e) => setStudentFormData({ ...studentFormData, name: e.target.value })}
                    className="w-full p-3 border-2 rounded-lg bg-white/5 border-border text-white"
                    placeholder={t('enterStudentName')}
                  />
                </div>
                <div>
                  <label className="block mb-2 font-bold text-gold uppercase tracking-wide text-sm">
                    Email:
                  </label>
                  <input
                    type="email"
                    value={studentFormData.email}
                    onChange={(e) => setStudentFormData({ ...studentFormData, email: e.target.value })}
                    className="w-full p-3 border-2 rounded-lg bg-white/5 border-border text-white"
                    placeholder="Введите email"
                  />
                </div>
                <div>
                  <label className="block mb-2 font-bold text-gold uppercase tracking-wide text-sm">
                    {t('password')}:
                  </label>
                  <input
                    type="password"
                    value={studentFormData.password}
                    onChange={(e) => setStudentFormData({ ...studentFormData, password: e.target.value })}
                    className="w-full p-3 border-2 rounded-lg bg-white/5 border-border text-white"
                    placeholder="Введите пароль"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleCreateStudent}
                    className="px-6 py-2 bg-mint/15 border border-mint/30 hover:bg-mint/25 text-mint rounded-lg font-bold transition"
                  >
                    {t('create')}
                  </button>
                  <button
                    onClick={() => {
                      setShowStudentForm(false);
                      setStudentFormData({ name: '', email: '', password: '' });
                    }}
                    className="px-6 py-2 bg-card border border-border hover:border-coral text-muted rounded-lg font-bold transition"
                  >
                    {t('cancel')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {students.length === 0 ? (
            <div className="text-center py-12 text-muted">
              <Users size={48} className="mx-auto mb-4 opacity-50" />
              <p className="font-bold">{t('noRegisteredStudents')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {students.map((student) => (
                <div
                  key={student.id}
                  className="concept-card rounded-lg p-4 flex items-center justify-between hover:border-mint/50 transition"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-bold text-lg text-white">
                        {student.name}
                      </h3>
                      {student.blocked && (
                        <span className="bg-coral text-white text-xs px-2 py-1 rounded font-bold">
                          ЗАБЛОКИРОВАН
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted">
                      📧 {student.email}
                    </p>
                    <p className="text-xs text-muted mt-1">
                      Зарегистрирован: {new Date(student.createdAt).toLocaleDateString('ru-RU')}
                    </p>
                    <p className="text-xs text-muted mt-1">
                      Telegram: {student.telegramChatId ? `привязан (${student.telegramUsername || 'без username'})` : 'не привязан'}
                    </p>
                    <p className="text-xs text-muted mt-1">
                      Доступ к боту: {student.telegramBotEnabled ? 'включен' : 'выключен'}
                    </p>
                    {student.telegramChatId && <BotUsageSummary student={student} />}
                  </div>

                  <div className="flex gap-2 items-center flex-wrap justify-end">
                    <button
                      onClick={() => handleToggleTelegramAccess(student)}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg font-bold text-sm transition bg-card border border-border hover:border-gold/40 text-white"
                      title="Включить или отключить доступ к Telegram-боту"
                      aria-label={`Доступ к Telegram-боту для ${student.name}`}
                    >
                      <span className="text-xs text-muted">Бот</span>
                      <span
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${student.telegramBotEnabled ? 'bg-mint/80' : 'bg-gray-500/60'}`}
                      >
                        <span
                          className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${student.telegramBotEnabled ? 'translate-x-5' : 'translate-x-1'}`}
                        />
                      </span>
                      <span className={`text-xs ${student.telegramBotEnabled ? 'text-mint' : 'text-gold'}`}>
                        {student.telegramBotEnabled ? 'ВКЛ' : 'ВЫКЛ'}
                      </span>
                    </button>

                    {!student.blocked ? (
                      <button
                        onClick={() => handleBlockStudent(student.id)}
                        className="flex items-center gap-2 px-4 py-2 bg-coral/15 border border-coral/30 hover:bg-coral/25 text-coral rounded-lg font-bold text-sm transition"
                        title="Заблокировать студента"
                      >
                        <Lock size={16} />
                        Заблокировать
                      </button>
                    ) : (
                      <button
                        onClick={() => handleUnblockStudent(student.id)}
                        className="flex items-center gap-2 px-4 py-2 bg-mint/15 border border-mint/30 hover:bg-mint/25 text-mint rounded-lg font-bold text-sm transition"
                        title="Разблокировать студента"
                      >
                        <Unlock size={16} />
                        Разблокировать
                      </button>
                    )}
                    
                    {user?.role === 'admin' && (
                      <button
                        onClick={() => handleDeleteStudent(student.id, student.name)}
                        className="p-2 bg-card border border-border hover:border-coral text-coral hover:text-red-400 rounded transition"
                        title="Удалить студента"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {studentsTotal !== null && students.length < studentsTotal && (
                <div className="mt-4 text-center">
                  <button
                    onClick={() => fetchStudents(false)}
                    disabled={loadingStudents}
                    className="px-4 py-2 bg-gold/15 border border-gold/30 text-gold rounded font-bold hover:bg-gold/25 transition"
                  >
                    {loadingStudents ? t('loading') : `${t('loadMore')} (${students.length}/${studentsTotal})`}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Teachers Tab (Admin only) */}
      {activeTab === 'teachers' && user?.role === 'admin' && (
        <div className="concept-card rounded-lg p-8 mb-8">
          {/* Teacher Count Widget */}
          <div className="concept-card p-6 rounded-lg mb-6">
            <p className="text-muted text-sm font-bold uppercase tracking-wide">{t('totalTeachers')}</p>
            <p className="text-3xl font-bold text-coral">{teachers.length}</p>
          </div>

          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gold">
              {t('manageTeachers')}
            </h2>
            <button
              onClick={() => setShowTeacherForm(!showTeacherForm)}
              className="flex items-center gap-2 px-4 py-2 bg-coral/15 border border-coral/30 hover:bg-coral/25 text-coral rounded-lg font-bold transition"
            >
              <Plus size={20} />
              {t('createTeacher')}
            </button>
          </div>

          {/* Create Teacher Form */}
          {showTeacherForm && (
            <div className="concept-card rounded-lg p-6 mb-6">
              <h3 className="text-xl font-bold mb-4 text-gold">
                {t('createNewTeacher')}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block mb-2 font-bold text-gold uppercase tracking-wide text-sm">
                    {t('teacherName')}:
                  </label>
                  <input
                    type="text"
                    value={teacherFormData.name}
                    onChange={(e) => setTeacherFormData({ ...teacherFormData, name: e.target.value })}
                    className="w-full p-3 border-2 rounded-lg bg-white/5 border-border text-white"
                    placeholder={t('enterTeacherName')}
                  />
                </div>
                <div>
                  <label className="block mb-2 font-bold text-gold uppercase tracking-wide text-sm">
                    Email:
                  </label>
                  <input
                    type="email"
                    value={teacherFormData.email}
                    onChange={(e) => setTeacherFormData({ ...teacherFormData, email: e.target.value })}
                    className="w-full p-3 border-2 rounded-lg bg-white/5 border-border text-white"
                    placeholder={t('enterEmail')}
                  />
                </div>
                <div>
                  <label className="block mb-2 font-bold text-gold uppercase tracking-wide text-sm">
                    {t('password')}:
                  </label>
                  <input
                    type="password"
                    value={teacherFormData.password}
                    onChange={(e) => setTeacherFormData({ ...teacherFormData, password: e.target.value })}
                    className="w-full p-3 border-2 rounded-lg bg-white/5 border-border text-white"
                    placeholder={t('enterPassword')}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleCreateTeacher}
                    className="px-6 py-2 bg-mint/15 border border-mint/30 hover:bg-mint/25 text-mint rounded-lg font-bold transition"
                  >
                    Создать
                  </button>
                  <button
                    onClick={() => {
                      setShowTeacherForm(false);
                      setTeacherFormData({ name: '', email: '', password: '' });
                    }}
                    className="px-6 py-2 bg-card border border-border hover:border-coral text-muted rounded-lg font-bold transition"
                  >
                    Отмена
                  </button>
                </div>
              </div>
            </div>
          )}

          {teachers.length === 0 ? (
            <div className="text-center py-12 text-muted">
              <Users size={48} className="mx-auto mb-4 opacity-50" />
              <p className="font-bold">{t('noRegisteredTeachers')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {teachers.map((teacher) => (
                <div
                  key={teacher.id}
                  className="concept-card rounded-lg p-4 flex items-center justify-between hover:border-coral/50 transition"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-bold text-lg text-white">
                        {teacher.name}
                      </h3>
                      <span className="bg-gold/20 text-gold text-xs px-2 py-1 rounded font-bold">
                        УЧИТЕЛЬ
                      </span>
                      {teacher.blocked && (
                        <span className="bg-coral text-white text-xs px-2 py-1 rounded font-bold">
                          ЗАБЛОКИРОВАН
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted">
                      📧 {teacher.email}
                    </p>
                    <p className="text-xs text-muted mt-1">
                      Создан: {new Date(teacher.createdAt).toLocaleDateString('ru-RU')}
                    </p>
                  </div>

                  <div className="flex gap-2">
                    {!teacher.blocked ? (
                      <button
                        onClick={() => handleBlockTeacher(teacher.id)}
                        className="flex items-center gap-2 px-4 py-2 bg-coral/15 border border-coral/30 hover:bg-coral/25 text-coral rounded-lg font-bold text-sm transition"
                        title="Заблокировать учителя"
                      >
                        <Lock size={16} />
                        Заблокировать
                      </button>
                    ) : (
                      <button
                        onClick={() => handleUnblockTeacher(teacher.id)}
                        className="flex items-center gap-2 px-4 py-2 bg-mint/15 border border-mint/30 hover:bg-mint/25 text-mint rounded-lg font-bold text-sm transition"
                        title="Разблокировать учителя"
                      >
                        <Unlock size={16} />
                        Разблокировать
                      </button>
                    )}
                    
                    <button
                      onClick={() => handleDeleteTeacher(teacher.id, teacher.name)}
                      className="p-2 bg-card border border-border hover:border-coral text-coral hover:text-red-400 rounded transition"
                      title="Удалить учителя"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Security Tab (Admin only) */}
      {activeTab === 'security' && user?.role === 'admin' && (
        <TwoFactorSettings />
      )}

      {showExerciseEditor && (
        <ExerciseEditor
          exercise={editingExercise}
          topicId={showExerciseEditor}
          onClose={() => {
            setShowExerciseEditor(null);
            setEditingExercise(null);
          }}
          onSave={handleExerciseSaved}
        />
      )}
    </div>
  );
};
